import { createFileRoute } from '@tanstack/react-router';
import { useForm, FormProvider } from 'react-hook-form';
import type { z as zod } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { Button } from '@tremor/react';
import { Trans } from '@lingui/macro';

import { Wrapper, ArticleForm } from '@/components';
import { handleFormError } from '@/utils';
import {
  useArticleSuspense,
  useUpdateArticle,
  getArticleQueryKey,
  getArticlesQueryKey,
  getArticleSuspenseQueryOptions,
  useAudiences,
} from '@/api';
import type { ArticleCrud } from '@/api/model';
import { articleSchema } from '@/schemas';

export const Route = createFileRoute(
  '/_authenticated/articles/$articleId/edit',
)({
  loader: async ({ context: { queryClient }, params: { articleId } }) =>
    queryClient.ensureQueryData(
      getArticleSuspenseQueryOptions(Number(articleId)),
    ),
  component: EditArticle,
});

function EditArticle() {
  const articleId = Route.useParams({
    select: ({ articleId }) => Number(articleId),
  });
  const { data: article } = useArticleSuspense(articleId);
  const { data: audienceData } = useAudiences();
  const { audiences = [] } = audienceData ?? {};
  const { queryClient } = Route.useRouteContext({
    select: ({ queryClient }) => ({ queryClient }),
  });
  const { mutateAsync } = useUpdateArticle({
    mutation: {
      async onSuccess() {
        await queryClient.invalidateQueries({
          queryKey: getArticlesQueryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: getArticleQueryKey(articleId),
        });
      },
    },
  });

  const methods = useForm<zod.infer<typeof articleSchema>>({
    resolver: zodResolver(articleSchema),
    defaultValues: {
      ...article,
      image: article.image?.versions?.large?.url,
      imageAlt: article.imageAlt ?? '',
      audienceIds: article.audiences?.map((audience) => audience.id.toString()),
      documents: article.documents?.map((document) => ({
        ...document,
        documentId: document.id,
      })),
    } as unknown as ArticleCrud,
  });

  const onSave = methods.handleSubmit(async (data) => {
    try {
      const response = await mutateAsync({
        id: article.id,
        data: {
          ...data,
          image: data.image instanceof File ? data.image : undefined,
          documents: data.documents?.map((document) => ({
            id: document.documentId,
            name: document.name,
            file: document.file instanceof File ? document.file : undefined,
            _destroy: document._destroy,
          })),
        },
      });
      methods.reset({
        ...response,
        image: response.image?.versions?.large?.url ?? null,
        imageAlt: response.imageAlt ?? '',
        audienceIds: response.audiences?.map((audience) =>
          audience.id.toString(),
        ),
        documents: response.documents?.map((document) => ({
          ...document,
          documentId: document.id,
        })),
      } as unknown as ArticleCrud);
      toast.success(<Trans>Article updated successfully!</Trans>);
    } catch (error) {
      handleFormError(error, methods.setError);
    }
  });

  return (
    <Wrapper
      header={<Trans>Edit article</Trans>}
      backLink='/articles'
      extraActions={
        <Button onClick={onSave}>
          <Trans>Save</Trans>
        </Button>
      }
    >
      <FormProvider {...methods}>
        <ArticleForm audiences={audiences} />
      </FormProvider>
    </Wrapper>
  );
}
